import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { City } from 'src/app/models/City';
import { AuthConfig } from 'src/app/models/AuthConfig';
import { MunicipiosService } from '../../services/municipios.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-municipio',
  templateUrl: './municipio.component.html',
  styleUrls: ['./municipio.component.css']
})
export class MunicipioComponent implements OnInit {

  private static readonly AUTH_REASON_MESSAGES: Record<string, { title?: string, body: string }> = {
    member_blocked: {
      title: 'Acesso Não Autorizado',
      body: 'O seu login não possui autorização prévia para acessar esta federação. O cadastro encontra-se bloqueado para esta conta. Se você considera que isso é um engano ou precisa de permissão, entre em contato com o administrador do sistema.'
    },
    member_not_found: {
      title: 'Cadastro não localizado',
      body: 'Sua autenticação foi realizada com sucesso, mas este usuário não possui cadastro nesta organização. Por favor, entre em contato com o seu administrador para solicitar a vinculação da sua conta.'
    },
    cpf_conflict: {
      body: 'O CPF informado no login corporativo já está vinculado a outro membro da federação. Entre em contato com o administrador da sua entidade.'
    }
  };

  @Input() originSystem: string | undefined;

  @Input() municipio!: string;
  @Input() uf!: string;
  @Output() municipioSelecionado = new EventEmitter<City>();

  dadosMunicipio!: City;
  brasao!: any;
  showBrasao = true;
  informacoes!: any;
  siteOficial!: string;

  authErrorTitle: string | null = null;
  authErrorMessage: string | null = null;
  authMessageIsError = false;

  keysToShowInOrder = [

    { key: 'nome', title: "Nome" },
    { key: 'sigla', title: "Estado" },
    { key: 'Mesorregião', title: 'Mesorregião' },
    { key: 'Microrregião', title: 'Microrregião' },
    { key: 'População*', title: 'População' },
    { key: 'Área', title: "Área" },
    { key: 'Densidade Demográfica', title: 'Densidade Demográfica' }

  ]

  constructor(private service: MunicipiosService, private titleService: Title) { }

  ngOnInit(): void {
    this.applyReasonMessage();
    this.service.getCityByUfAndName(this.municipio, this.uf).subscribe({
      next: data => {
        this.dadosMunicipio = data;
        this.getDadosMunicipio(data);
        this.dadosMunicipio.nomeUrl = this.transformNameInUrlPath(this.dadosMunicipio.nome)
        this.municipioSelecionado.emit(this.dadosMunicipio);
        this.titleService.setTitle(this.dadosMunicipio.nome || 'Portal do cidadão')
      },
      error: err => {
        if (err.status == 500) {
          const queryParams = new URLSearchParams();
          queryParams.append("from_redirect", "true");

          this.redirectTo(`${environment.app.brasilUrl}?${queryParams.toString()}`);
        }
      }
    });

    this.siteOficial = `http://${this.municipio}.${this.uf}.gov.br`;
  }

  createImageFromBlob(image: Blob) {
    let reader = new FileReader();
    reader.addEventListener("load", () => {
      this.brasao = reader.result;
    }, false);

    if (image && image.size == 0) {
      this.showBrasao = false;
      return;
    }
    reader.readAsDataURL(image);
  }

  getDadosMunicipio(municipio: City) {
    this.service.getDadosMunicipio(municipio.uf, municipio.codigoIBGE).subscribe(
      data => this.informacoes = this.transformarDados(JSON.parse(data.toString()))
    )
  }

  getValueByKey(key: string) {
    return this.informacoes[key]
  }

  private transformNameInUrlPath(name: any) {
    return name.normalize('NFD').replace(/[\u0300-\u036f]/g, "")
      .toLowerCase().replaceAll(" ", "");
  }

  private transformarDados(data: any) {
    return data;
  }

  private OAUTH_AUTHORIZE_ENDPOINT = `${environment.oauth.url}/oauth2/authorize`;

  private getRedirectUri(authConfig?: AuthConfig) {
    const authConfigRedirectUri = authConfig?.appUrl?.trim() || authConfig?.entryUrl?.trim();
    const defaultRedirectUri = authConfigRedirectUri || environment.oauth.redirectUri;

    if (!this.originSystem) {
      return defaultRedirectUri;
    }

    const fixedRedirect = environment.redirects[this.originSystem];
    if (fixedRedirect) {
      return fixedRedirect;
    }

    const directEntry = environment.directEntries[this.originSystem];
    if (directEntry && authConfig?.authMode === 'FEDERATED_OIDC') {
      return this.appendPath(defaultRedirectUri, directEntry.path);
    }

    return defaultRedirectUri;
  }

  private appendPath(baseUrl: string, path: string): string {
    const url = new URL(baseUrl);
    url.pathname = `/${path.replace(/^\/+/, '')}`;
    return url.toString();
  }

  onClick() {
    this.clearAuthMessage();
    const responseType = 'token';
    const clientId = environment.authentication.clientId;

    this.service.getAuthConfig(
      this.uf,
      this.municipio
    ).subscribe({
      next: (authConfig: AuthConfig) => {
        const redirectUri = this.getRedirectUri(authConfig);

        if (authConfig.authMode === 'FEDERATED_OIDC') {
          if (!authConfig.startUrl) {
            this.setAuthMessage(authConfig.message ?? 'Nao foi possivel iniciar a autenticacao federada', true);
            return;
          }

          this.redirectTo(
            this.buildFederatedAuthorizeUrl(authConfig.startUrl, responseType, clientId, redirectUri, this.originSystem)
          );
          return;
        }

        this.redirectToDefaultAuthorize(responseType, clientId, redirectUri);
      },
      error: (error) => {
        const redirectUri = this.getRedirectUri();

        if (error?.error?.failClosed !== true) {
          this.redirectToDefaultAuthorize(responseType, clientId, redirectUri);
          return;
        }
        this.setAuthMessage(error?.error?.message ?? 'Autenticacao federada indisponivel', true);
      }
    });
  }

  private applyReasonMessage() {
    const reason = new URLSearchParams(this.getLocationSearch()).get('reason');
    const message = reason ? MunicipioComponent.AUTH_REASON_MESSAGES[reason] : null;

    if (message) {
      this.setAuthMessage(message.body, true, message.title ?? null);
      this.removeReasonFromUrl();
    }
  }

  protected removeReasonFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('reason');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  private clearAuthMessage() {
    this.setAuthMessage(null, false);
  }

  private setAuthMessage(message: string | null, isError: boolean, title: string | null = null) {
    this.authErrorTitle = title;
    this.authErrorMessage = message;
    this.authMessageIsError = !!message && isError;
  }

  protected getLocationSearch() {
    return window.location.search;
  }

  private redirectToDefaultAuthorize(responseType: string, clientId: string, redirectUri: string) {
    this.service.getGovBrConfig(this.uf, this.municipio).subscribe({
      next: (data) => this.redirectTo(
        this.buildDefaultAuthorizeUrl(
          responseType,
          clientId,
          redirectUri,
          data?.encryptedGovBrConfig ?? undefined
        )
      ),
      error: () => this.redirectTo(this.buildDefaultAuthorizeUrl(responseType, clientId, redirectUri))
    });
  }

  private buildFederatedAuthorizeUrl(
    startUrl: string,
    responseType: string,
    clientId: string,
    redirectUri: string,
    originSystem?: string
  ) {
    const authorizeUrl = new URL(startUrl);
    this.applyAuthorizeParams(authorizeUrl.searchParams, responseType, clientId, redirectUri, originSystem);

    return authorizeUrl.toString();
  }

  private buildDefaultAuthorizeUrl(
    responseType: string,
    clientId: string,
    redirectUri: string,
    contextConfig?: string
  ) {
    const queryParams = new URLSearchParams();
    this.applyAuthorizeParams(queryParams, responseType, clientId, redirectUri);
    queryParams.append("bth_ignore_origin", "true");

    if (contextConfig) {
      queryParams.append("context_config", contextConfig);
    }

    return `${this.OAUTH_AUTHORIZE_ENDPOINT}?${queryParams.toString()}`;
  }

  private applyAuthorizeParams(
    params: URLSearchParams,
    responseType: string,
    clientId: string,
    redirectUri: string,
    originSystem?: string
  ) {
    params.set("response_type", responseType);
    params.set("client_id", clientId);
    params.set("redirect_uri", redirectUri);

    if (originSystem) {
      params.set("origin_system", originSystem);
      return;
    }

    params.delete("origin_system");
  }

  protected redirectTo(url: string) {
    window.location.assign(url);
  }
}
